import { ChevronDownIcon, Cog6ToothIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import * as Popover from '@radix-ui/react-popover';
import React from 'react';

import { PopoverContent, ScaleFadeIn } from './Animations';
import { cn } from './cn';

export interface FilterMenuItem {
  key: string;
  text: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  onClick?: () => void;
  canCheck?: boolean;
  disabled?: boolean;
}

export interface FilterSelectProps {
  buttonText?: React.ReactNode;
  buttonIcon?: React.ReactNode;
  iconOnly?: boolean;
  items: FilterMenuItem[];
  searchPlaceholder?: string;
  emptyText?: string;
  noResultsText?: string;
  bottomLinkText?: string;
  onBottomLinkClick?: () => void;
  isReadOnly?: boolean;
  className?: string;
}

export const SelectedCountBadge = ({ children }: { children: React.ReactNode }) => (
  <ScaleFadeIn
    className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-info-500 px-1.5 text-xs font-medium text-white"
    from={0.8}
    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
  >
    {children}
  </ScaleFadeIn>
);

export const FilterSelect = ({
  buttonText,
  buttonIcon,
  iconOnly = false,
  items,
  searchPlaceholder = 'Search...',
  emptyText = 'No items to select from.',
  noResultsText = 'No results found',
  bottomLinkText,
  onBottomLinkClick,
  isReadOnly = false,
  className,
}: FilterSelectProps) => {
  const [search, setSearch] = React.useState('');
  const filteredItems = React.useMemo(
    () => (search ? items.filter((item) => item.text.toLowerCase().includes(search.toLowerCase())) : items),
    [items, search]
  );

  const defaultIcon = iconOnly ? <Cog6ToothIcon className="h-3 w-3" /> : undefined;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        {iconOnly ? (
          <button
            className={cn(
              'inline-flex items-center justify-center rounded p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer bg-transparent border-none',
              className
            )}
            aria-label="Settings"
          >
            {buttonIcon || defaultIcon}
          </button>
        ) : (
          <button
            className={cn(
              'inline-flex items-center gap-1 rounded border border-gray-300 bg-surface px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer',
              className
            )}
          >
            {buttonText}
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-64 rounded-lg border border-border bg-surface shadow-lg"
          sideOffset={4}
          align="start"
          asChild
        >
          <PopoverContent>
            <div className="p-2 border-b border-border">
              <div className="flex items-center gap-2 rounded-md bg-gray-100 px-2.5 py-2">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto py-1">
              {items.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-gray-500">{emptyText}</p>
              ) : filteredItems.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-gray-500">{noResultsText}</p>
              ) : (
                filteredItems.map((item) => {
                  const canCheck = item.canCheck !== false;
                  if (canCheck && !isReadOnly) {
                    return (
                      <label
                        key={item.key}
                        className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-200 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={item.checked || false}
                          onChange={(e) => {
                            item.onChange?.(e.target.checked);
                            item.onClick?.();
                          }}
                          className="h-4 w-4 rounded border-gray-400 text-info-500 focus:ring-info-500"
                        />
                        <span className="truncate text-gray-800">{item.text}</span>
                      </label>
                    );
                  }
                  return (
                    <div key={item.key} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500">
                      <input
                        type="checkbox"
                        checked={item.checked || false}
                        disabled
                        className="h-4 w-4 rounded border-gray-400 opacity-50"
                      />
                      <span className="truncate">{item.text}</span>
                    </div>
                  );
                })
              )}
            </div>
            {bottomLinkText && onBottomLinkClick && (
              <div className="border-t border-border p-2">
                <button
                  onClick={onBottomLinkClick}
                  className="w-full text-left text-sm text-info-500 hover:text-info-600 cursor-pointer bg-transparent border-none p-1.5 transition-colors duration-200"
                >
                  {bottomLinkText}
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
